import { useContext, useEffect, useState } from 'react';
import SummaryPaymentFooter from 'Domains/Step/molecules/SummaryPaymentFooter';
import { WidgetContext } from 'Pages/Widget/Widget.constants';
import { WidgetActionType } from 'Pages/Widget/Widget.store';
import * as stripeJs from '@stripe/stripe-js';
import { useWidgetPriceQuote } from 'hooks/priceQuote';
import { SummaryStepProps } from './types';
import * as S from './styles';
import SummaryPayment from './organisms/SummaryPayment';
import SummaryData from './organisms/SummaryData';
import PriceChangedPopup from './organisms/PriceChangedPopup';
import { useGetPaymentCards, useProceedPayment } from './hooks';
import { getCardUiState } from './cardUi';
import { isProceedPaymentDisabled } from './paySequence';

const SummaryStep = ({ steps }: SummaryStepProps) => {
  const { dispatch } = useContext(WidgetContext);
  const { isFetching, paymentCards, selectedPaymentCardId, initialDefaultCard, onCardSelect, onCardAdded } =
    useGetPaymentCards();
  const { quote, isFetching: isQuoteFetching, isError: isQuoteError } = useWidgetPriceQuote();

  const { proceedPayment, isSubmissionReady, priceChange, priceChangeActions, forgetSetUpCard } = useProceedPayment({
    onError: () => {
      onCardSelect(initialDefaultCard);
    },
  });
  const cardUi = getCardUiState(quote);

  const [isNewCardIncomplete, setIsNewCardIncomplete] = useState(true);
  const [isProceedPaymentFetching, setIsProceedPaymentFetching] = useState(false);

  const isPaymentButtonDisabled = isProceedPaymentDisabled({
    quotePriceCents: quote?.priceCents,
    isQuoteFetching,
    isQuoteError,
    isSubmissionReady,
    selectedCardId: selectedPaymentCardId,
    isNewCardIncomplete,
  });

  useEffect(() => {
    if (!cardUi.showCards) {
      forgetSetUpCard();
    }
  }, [cardUi.showCards, forgetSetUpCard]);

  const handleGoBack = () => dispatch({ type: WidgetActionType.PREVIOUS_STEP });

  const onCardInfoChange = (event: stripeJs.StripeCardElementChangeEvent) => {
    forgetSetUpCard();

    if (event.complete) {
      setIsNewCardIncomplete(false);
      return;
    }
    if (event.error) {
      setIsNewCardIncomplete(true);
    }
  };

  const onProceedPaymentClick = async () => {
    if (isPaymentButtonDisabled) {
      return;
    }
    setIsProceedPaymentFetching(true);
    await proceedPayment(selectedPaymentCardId);
    setIsProceedPaymentFetching(false);
  };

  return (
    <S.Wrapper>
      <S.Container>
        <SummaryData />
        <SummaryPayment
          cardUi={cardUi}
          isLoading={isFetching}
          onCardInfoChange={onCardInfoChange}
          onCardSelect={onCardSelect}
          onCardAdded={onCardAdded}
          paymentCards={paymentCards}
          selectedPaymentCardId={selectedPaymentCardId}
          steps={steps}
        />
      </S.Container>
      <SummaryPaymentFooter
        loading={isProceedPaymentFetching}
        isDisabled={isPaymentButtonDisabled}
        onGoBackClick={handleGoBack}
        onProceedPaymentClick={onProceedPaymentClick}
      />
      {priceChange ? (
        <PriceChangedPopup
          open
          postedPriceCents={priceChange.postedPriceCents}
          serverPriceCents={priceChange.serverPriceCents}
          {...priceChangeActions}
        />
      ) : null}
    </S.Wrapper>
  );
};

export default SummaryStep;
